export interface AssetPackage {
  sceneUrl: string;
  xml: string;
  files: ReadonlyMap<string, Uint8Array>;
}

const packageCache = new Map<string, Promise<AssetPackage>>();

export function meshUsesFaceNormals(element: Element, files: ReadonlyMap<string, Uint8Array>): boolean {
  if (element.getAttribute("normal") || element.getAttribute("smoothnormal") === "true") return false;
  const file = element.getAttribute("file");
  if (!file) return true;
  if (file.toLowerCase().endsWith(".stl")) return true;
  if (!file.toLowerCase().endsWith(".obj")) return false;
  const bytes = files.get(file);
  if (!bytes) throw new Error("Mesh resource is missing");
  const source = new TextDecoder().decode(bytes);
  // OBJ defaults to flat faces. Do not invent smooth normals across capped sectors.
  const authoredNormals = /^\s*vn\s+\S+/m.test(source);
  const smoothingGroups = /^\s*s\s+(?!off(?:\s|$)|0(?:\s|$))\S+/m.test(source);
  return !authoredNormals && !smoothingGroups;
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) throw new Error(`资源加载失败（HTTP ${response.status}）`);
  return new Uint8Array(await response.arrayBuffer());
}

function fileReferences(xml: string): string[] {
  const documentNode = new DOMParser().parseFromString(xml, "application/xml");
  if (documentNode.querySelector("parsererror")) throw new Error("场景 XML 无法解析");
  const references = new Set<string>();
  for (const element of documentNode.querySelectorAll("[file]")) {
    const path = element.getAttribute("file");
    if (!path) continue;
    if (path.startsWith("/") || path.includes("\\") || path.split("/").includes("..")) {
      throw new Error("场景包含无效资源路径");
    }
    references.add(path);
  }
  return [...references];
}

async function fetchPackage(sceneUrl: string): Promise<AssetPackage> {
  const sceneBytes = await fetchBytes(sceneUrl);
  const xml = new TextDecoder().decode(sceneBytes);
  const references = fileReferences(xml);
  const baseUrl = new URL(".", new URL(sceneUrl, window.location.href));
  const files = new Map<string, Uint8Array>();
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < references.length) {
      const index = cursor;
      cursor += 1;
      const reference = references[index];
      if (reference === undefined) continue;
      files.set(reference, await fetchBytes(new URL(reference, baseUrl).toString()));
    }
  }

  await Promise.all(Array.from({ length: Math.min(8, references.length) }, () => worker()));
  return { sceneUrl, xml, files };
}

export function loadAssetPackage(sceneUrl: string): Promise<AssetPackage> {
  let pending = packageCache.get(sceneUrl);
  if (!pending) {
    pending = fetchPackage(sceneUrl).catch((error: unknown) => {
      packageCache.delete(sceneUrl);
      throw error;
    });
    packageCache.set(sceneUrl, pending);
  }
  return pending;
}

export async function prefetchAssetPackage(sceneUrl: string): Promise<void> {
  await loadAssetPackage(sceneUrl);
}

export function releaseAssetPackage(sceneUrl: string): void {
  packageCache.delete(sceneUrl);
}
